import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { addToCart } from '../redux/addCart/cartSlice';
import { toast } from 'react-toastify';
import {
  getAllFavouriteRequest,
  getAvailableProducts,
  getAvailableSelectedProductGroupId,
  getProducts,
} from '../redux/pos/posSlice';
import { getImagePath } from '../constants/defaultValues';
import { useThemeColors } from '../hooks/useThemeColors';
import ProductModal from './ProductModal';
import { FaPlus } from 'react-icons/fa';
import { getCurrentTenant, getCurrentUser } from '../redux/auth/authSlice';
import FavoriteButton from './common/FavouriteButton';

const MenuItems = ({ orderType, darkMode }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const dispatch = useDispatch();

  // Central Hook for Colors
  const { contentBg, cardBg, textColor, subTextColor, borderColor, isDark } =
    useThemeColors(darkMode);

  const selectedProductGroupId = useSelector(
    getAvailableSelectedProductGroupId
  );
  const products = useSelector(getAvailableProducts);
  const user = useSelector(getCurrentUser);
  const currentTenant = useSelector(getCurrentTenant);

  useEffect(() => {
    dispatch(getProducts({ productGroupId: selectedProductGroupId }));

    if (user?.referenceId && currentTenant?.id) {
      dispatch(
        getAllFavouriteRequest({
          userId: user.referenceId,
          tenantId: currentTenant.id,
        })
      );
    }
  }, [selectedProductGroupId, dispatch, user, currentTenant]);

  const handleAddToCartClick = (e, item) => {
    e.stopPropagation();
    dispatch(addToCart({ ...item, orderType }));
    toast.success(`✅ ${item.name} added!`);
  };

  const handleAddToCart = (product) => {
    dispatch(addToCart({ ...product, orderType }));
    toast.success(`✅ ${product.name} added to cart`);
    setSelectedProduct(null);
  };

  return (
    <div
      className="rounded-3 overflow-hidden shadow-lg"
      style={{
        backgroundColor: contentBg,
        minHeight: '80vh',
        transition: 'all 0.3s ease',
      }}
    >
      <PerfectScrollbar
        style={{ height: '80vh' }}
        options={{ wheelPropagation: true }}
      >
        <div className="container-fluid py-4">
          <div className="row g-4">
            {products && products.length > 0 ? (
              products.map((item) => (
                <div
                  key={item.id}
                  className="product-card col-12 col-md-6 col-lg-4"
                >
                  <div
                    className="card h-100 border-0 shadow-sm position-relative overflow-hidden"
                    style={{
                      backgroundColor: cardBg,
                      borderRadius: '16px', // Extra rounded corners as per image
                      transition: 'transform 0.3s ease, shadow 0.3s ease',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedProduct(item)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow =
                        '0 10px 20px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div>
                      <FavoriteButton
                        productId={item.id}
                        item={item}
                        darkMode={isDark}
                      />
                    </div>

                    {/* Image Section - Edge to Edge */}
                    <div
                      className="overflow-hidden"
                      style={{
                        height: '200px',
                        width: '100%',
                        backgroundColor: isDark ? '#2a2a2a' : '#f1f1f1',
                      }}
                    >
                      {item.image?.fileToken ? (
                        <img
                          src={`${getImagePath}${item.image.fileToken}`}
                          className="w-100 h-100 object-fit-cover card-img-effect"
                          alt={item.name}
                          style={{ transition: '0.5s ease' }}
                        />
                      ) : (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted small">
                          No Image Available
                        </div>
                      )}
                    </div>

                    {/* Content Section */}
                    <div className="card-body d-flex flex-column p-3">
                      <h5
                        className="fw-bold mb-1"
                        style={{
                          color: textColor,
                          fontSize: '1.15rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.name}
                      </h5>

                      <p
                        className="small mb-3"
                        style={{
                          color: subTextColor,
                          lineHeight: '1.4',
                          display: '-webkit-box',
                          WebkitLineClamp: '2',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          height: '38px',
                        }}
                      >
                        {item.shopDescription ||
                          'Freshly prepared with the finest ingredients.'}
                      </p>

                      <div className="d-flex justify-content-between align-items-center mt-auto">
                        <div
                          className="fs-5 fw-bold"
                          style={{ color: textColor }}
                        >
                          <span
                            style={{ fontSize: '0.9rem', marginRight: '2px' }}
                          >
                            Rs
                          </span>
                          {Number(item.shopPrice).toLocaleString()}
                        </div>

                        <button
                          className="btn btn-danger rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                          style={{
                            width: '38px',
                            height: '38px',
                            padding: '0',
                            border: 'none',
                            transition: '0.2s',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToCartClick(e, item);
                          }}
                        >
                          <FaPlus size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center py-5">
                <p style={{ color: subTextColor }}>
                  No items available at the moment.
                </p>
              </div>
            )}
          </div>
        </div>
      </PerfectScrollbar>

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={handleAddToCart}
          orderType={orderType}
          darkMode={isDark}
        />
      )}
    </div>
  );
};

export default MenuItems;
